// Recharts wrappers shared by every Explore-stats page.
// recharts ^3.6.0 is already a project dependency. Keep these generic so a new
// stats page just feeds { data, keys } and gets a consistent, themed chart.

import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  ScatterChart, Scatter, ZAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

// Recharts is plain SVG and doesn't read the MUI theme, so each chart pulls
// axis / grid / tooltip colours from the active theme to stay legible in dark
// mode (#159). Returns the colour set + ready-to-spread Tooltip props.
function useChartColors() {
  const theme = useTheme();
  const { palette } = theme;
  return {
    axis: palette.text.secondary,
    tick: palette.text.secondary,
    grid: palette.divider,
    legend: palette.text.primary,
    tooltip: {
      contentStyle: {
        backgroundColor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        borderRadius: 4,
        color: palette.text.primary,
        fontSize: 12,
      },
      labelStyle: { color: palette.text.primary },
      itemStyle: { color: palette.text.primary },
    },
  };
}

// Shared categorical palette (party-ish but generic).
export const PALETTE = [
  '#C62828', '#1565C0', '#2E7D32', '#6A1B9A', '#EF6C00',
  '#00838F', '#AD1457', '#558B2F', '#4527A0', '#D84315',
];

const AXIS = { fontSize: 12 };

// Deterministic party → colour. Known major parties get house colours;
// anything else hashes stably into the palette.
const KNOWN_PARTY_COLOURS = {
  'Australian Labor Party': '#C62828',
  'Liberal Party of Australia': '#1565C0',
  'The Nationals': '#0B6B3A',
  'National Party of Australia': '#0B6B3A',
  'Australian Greens': '#2E7D32',
  Independent: '#6A1B9A',
  "Pauline Hanson's One Nation": '#EF6C00',
  "Katter's Australian Party": '#8D6E63',
  'Centre Alliance': '#00838F',
  'Australian Democrats': '#AD1457',
};

export function partyColor(party) {
  if (!party) return '#9E9E9E';
  if (KNOWN_PARTY_COLOURS[party]) return KNOWN_PARTY_COLOURS[party];
  let h = 0;
  for (let i = 0; i < party.length; i += 1) h = (h * 31 + party.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

// Australian parliamentary convention: House of Representatives = green,
// Senate = red. Applied to any chart series keyed by chamber so the chambers
// keep the same colour site-wide regardless of series order. Combined /
// neutral keys (All, Joint…) get distinct non-green/red colours so they never
// collide with a chamber line.
const CHAMBER_COLOURS = {
  house: '#2E7D32',
  'house of representatives': '#2E7D32',
  representatives: '#2E7D32',
  reps: '#2E7D32',
  'federation chamber': '#66BB6A',
  senate: '#C62828',
  senator: '#C62828',
  senators: '#C62828',
  joint: '#6A1B9A',
  all: '#546E7A',
  both: '#546E7A',
  'both chambers': '#546E7A',
  total: '#546E7A',
  overall: '#546E7A',
};

export function chamberColor(key) {
  if (key == null) return undefined;
  return CHAMBER_COLOURS[String(key).trim().toLowerCase()];
}

// Colour for a multi-series key: chamber-aware first, else positional palette.
function seriesColor(key, index) {
  return chamberColor(key) || PALETTE[index % PALETTE.length];
}

/**
 * Single-series bar chart.
 * data: [{ [xKey]: label, [yKey]: number }]
 */
export function StatBar({ data, xKey, yKey, height = 320, layout = 'horizontal', color }) {
  const vertical = layout === 'vertical';
  const c = useChartColors();
  const tick = { ...AXIS, fill: c.tick };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={layout}
                margin={{ top: 8, right: 16, bottom: 8, left: vertical ? 80 : 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
        {vertical
          ? (<><XAxis type="number" tick={tick} stroke={c.axis} /><YAxis type="category" dataKey={xKey} tick={tick} stroke={c.axis} width={150} /></>)
          : (<><XAxis dataKey={xKey} tick={tick} stroke={c.axis} /><YAxis tick={tick} stroke={c.axis} /></>)}
        <Tooltip {...c.tooltip} cursor={{ fill: c.grid }} />
        <Bar dataKey={yKey} fill={color || PALETTE[1]} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Stacked bar chart across multiple series keys.
 * data: [{ [xKey]: label, key1: n, key2: n, ... }]; keys: ['key1','key2',...]
 */
export function StatStackedBar({
  data, xKey, keys, height = 360, layout = 'horizontal', percent = false,
}) {
  const vertical = layout === 'vertical';
  const c = useChartColors();
  const tick = { ...AXIS, fill: c.tick };
  // percent mode: renormalise every row so its keys sum to 100, giving a
  // 100%-stacked chart where each column/bar fills the axis.
  const plotData = useMemo(() => {
    if (!percent) return data || [];
    return (data || []).map((r) => {
      const total = keys.reduce((s, k) => s + (Number(r[k]) || 0), 0) || 1;
      const out = { ...r };
      keys.forEach((k) => {
        out[k] = Math.round(((Number(r[k]) || 0) / total) * 1000) / 10; // 1dp %
      });
      return out;
    });
  }, [data, keys, percent]);
  const numAxis = percent
    ? { type: 'number', domain: [0, 100], unit: '%', tick, stroke: c.axis }
    : { type: 'number', tick, stroke: c.axis };
  const tipFmt = percent ? { formatter: (v) => `${v}%` } : {};
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={plotData} layout={layout}
                margin={{ top: 8, right: 16, bottom: 8, left: vertical ? 100 : 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
        {vertical
          ? (<><XAxis {...numAxis} /><YAxis type="category" dataKey={xKey} tick={tick} stroke={c.axis} width={170} /></>)
          : (<><XAxis dataKey={xKey} tick={tick} stroke={c.axis} /><YAxis {...numAxis} /></>)}
        <Tooltip {...c.tooltip} {...tipFmt} cursor={{ fill: c.grid }} />
        <Legend wrapperStyle={{ fontSize: 12, color: c.legend }} />
        {keys.map((k, i) => (
          <Bar key={k} dataKey={k} stackId="a" fill={seriesColor(k, i)} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Multi-series line chart.
 * data: [{ [xKey]: label, key1: n, ... }]; keys: ['key1', ...]
 */
export function StatLine({ data, xKey, keys, height = 340 }) {
  const c = useChartColors();
  const tick = { ...AXIS, fill: c.tick };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
        <XAxis dataKey={xKey} tick={tick} stroke={c.axis} />
        <YAxis tick={tick} stroke={c.axis} />
        <Tooltip {...c.tooltip} />
        <Legend wrapperStyle={{ fontSize: 12, color: c.legend }} />
        {keys.map((k, i) => (
          <Line key={k} type="monotone" dataKey={k}
                stroke={seriesColor(k, i)} dot={false} strokeWidth={2} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Stacked area chart across multiple series keys (e.g. % women/men).
 * data: [{ [xKey]: label, key1: n, key2: n }]; keys: ['key1','key2']
 */
export function StatStackedArea({ data, xKey, keys, height = 340, unit }) {
  const c = useChartColors();
  const tick = { ...AXIS, fill: c.tick };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
        <XAxis dataKey={xKey} tick={tick} stroke={c.axis} />
        <YAxis tick={tick} stroke={c.axis} unit={unit} />
        <Tooltip {...c.tooltip} />
        <Legend wrapperStyle={{ fontSize: 12, color: c.legend }} />
        {keys.map((k, i) => (
          <Area key={k} type="monotone" dataKey={k} stackId="a"
                stroke={seriesColor(k, i)}
                fill={seriesColor(k, i)} fillOpacity={0.7} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

/**
 * Histogram (single series, banded x). Alias of StatBar with band styling.
 * data: [{ [xKey]: band, [yKey]: count }]
 */
export function StatHistogram({ data, xKey, yKey, height = 320, color }) {
  const c = useChartColors();
  const tick = { ...AXIS, fill: c.tick };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }} barCategoryGap={2}>
        <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
        <XAxis dataKey={xKey} tick={tick} stroke={c.axis} />
        <YAxis tick={tick} stroke={c.axis} />
        <Tooltip {...c.tooltip} cursor={{ fill: c.grid }} />
        <Bar dataKey={yKey} fill={color || PALETTE[2]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * 100%-stacked horizontal bar. Each row sums to its real total; this
 * component renormalises every row to 100% and shows the share each
 * `categories` key takes. Hoisted from TimeOnBusiness.js (#213) so the
 * Floor-time pages (#215 / #216) and any future per-period stacked
 * share view can reuse a single implementation.
 *
 * rows:        [{ label?: string, chamber?, date?, ...numeric category values }]
 * categories:  list of numeric keys to stack
 * labelKey:    field to render on the y-axis (defaults to a `chamber\ndate`
 *              composite to match the floor-time pattern).
 */
export function HundredPctStacked({
  rows, categories, height = 480, labelKey, leftMargin = 140,
}) {
  const theme = useTheme();
  const data = useMemo(() => {
    return (rows || []).map((r) => {
      const total = categories.reduce((s, c) => s + (r[c] || 0), 0) || 1;
      const out = {
        label: labelKey
          ? r[labelKey]
          : (r.chamber || r.date)
              ? `${r.chamber || ''}${r.chamber && r.date ? '\n' : ''}${r.date || ''}`
              : '',
      };
      categories.forEach((c) => {
        out[c] = Math.round(((r[c] || 0) / total) * 1000) / 10;  // % to 1dp
      });
      return out;
    });
  }, [rows, categories, labelKey]);

  const tick = { fontSize: 11, fill: theme.palette.text.secondary };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical"
                margin={{ top: 8, right: 16, bottom: 8, left: leftMargin }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis type="number" domain={[0, 100]} unit="%" tick={tick}
               stroke={theme.palette.text.secondary} />
        <YAxis type="category" dataKey="label" tick={tick}
               stroke={theme.palette.text.secondary} width={leftMargin - 10} />
        <Tooltip contentStyle={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary, fontSize: 12,
        }} formatter={(v) => `${v}%`} />
        <Legend wrapperStyle={{ fontSize: 11, color: theme.palette.text.primary }} />
        {categories.map((c, i) => (
          <Bar key={c} dataKey={c} stackId="a"
               fill={seriesColor(c, i)} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}


/**
 * Bubble cloud — one point per item, size ∝ `zKey` value.
 * data: [{ name, ...,  [zKey]: number }]. Lays points out in a grid and
 * sizes/colours each by its value (used by "Periods of service").
 */
export function StatBubble({ data, zKey = 'years', height = 460, color }) {
  const c = useChartColors();
  const cols = Math.ceil(Math.sqrt(data.length || 1));
  const points = data.map((d, i) => ({
    ...d,
    x: i % cols,
    y: Math.floor(i / cols),
    z: d[zKey],
  }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <XAxis type="number" dataKey="x" hide />
        <YAxis type="number" dataKey="y" hide reversed />
        <ZAxis type="number" dataKey="z" range={[20, 600]} />
        <Tooltip
          cursor={{ strokeDasharray: '3 3', stroke: c.grid }}
          content={({ payload }) => {
            if (!payload || !payload.length) return null;
            const p = payload[0].payload;
            return (
              <div style={{ ...c.tooltip.contentStyle, padding: 8 }}>
                <strong>{p.name}</strong><br />
                {p.party}{p.state ? ` — ${p.state}` : ''}<br />
                {p[zKey]} years
              </div>
            );
          }}
        />
        <Scatter data={points} fill={color || PALETTE[1]} fillOpacity={0.55} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}
